import { applyQueueAssign, applyQueueMove } from '@chiroup/core/functions/connectorQueueCache';
import {
  QueueDisposition,
  UnroutedExchangeSummary,
  UnroutedQueueList,
} from '@chiroup/core/types/ConnectorAdmin.type';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import connectionApiService, {
  ConnectionApiConsole,
  UnroutedDispositionChangeResult,
} from '../services/connectionApi.service';

const KEY_BASE = ['connection-api'] as const;
/** ONE query paints the console; every mutation invalidates it. */
const CONSOLE_KEY = [...KEY_BASE, 'console'] as const;

/**
 * Invalidate the ONE console query (key stays module-private). For the rare
 * spots that need a re-read outside a mutation — e.g. TracePanel's
 * natural-expiry timeout flipping the session label off.
 */
export const useInvalidateConsole = () => {
  const queryClient = useQueryClient();

  return () => queryClient.invalidateQueries(CONSOLE_KEY);
};

export const useConnectionApiConsole = (
  includeReviewed = false,
  /**
   * A kicked manual run re-reads the console on this interval until its
   * durable runId reaches completed/failed. Off when no requested run is active.
   */
  refetchInterval: number | false = false
) =>
  useQuery(
    [...CONSOLE_KEY, includeReviewed],
    () => connectionApiService.consoleStatus(includeReviewed),
    { refetchOnWindowFocus: false, retry: false, refetchInterval }
  );

/** State-changing ops re-read the (single) console query. */
const useConsoleMutation = <TArgs, TResult>(fn: (args: TArgs) => Promise<TResult>) => {
  const queryClient = useQueryClient();

  return useMutation<TResult, unknown, TArgs>(fn, {
    onSuccess: () => queryClient.invalidateQueries(CONSOLE_KEY),
  });
};

const useVendorsMutation = useConsoleMutation;

export const useRunCredentialCheck = () =>
  useVendorsMutation((vendor?: string) => connectionApiService.runCredentialCheck(vendor));

export const useStageCandidate = () =>
  useVendorsMutation(
    ({
      vendor,
      integrationId,
      apiKey,
    }: {
      vendor: string;
      integrationId: number;
      apiKey: string;
    }) => connectionApiService.stageCandidate(vendor, integrationId, apiKey)
  );

export const useValidateCandidate = () =>
  useVendorsMutation(({ vendor, integrationId }: { vendor: string; integrationId: number }) =>
    connectionApiService.validateCandidate(vendor, integrationId)
  );

export const usePromoteCandidate = () =>
  useVendorsMutation(({ vendor, integrationId }: { vendor: string; integrationId: number }) =>
    connectionApiService.promoteCandidate(vendor, integrationId)
  );

export const useRollbackCredentials = () =>
  useVendorsMutation(({ vendor, integrationId }: { vendor: string; integrationId: number }) =>
    connectionApiService.rollback(vendor, integrationId)
  );

const UNROUTED_KEY = [...KEY_BASE, 'unrouted'] as const;

/** One query per queue (item 4): only the visible disposition is fetched. */
export const useUnroutedExchanges = (disposition: QueueDisposition) =>
  useQuery([...UNROUTED_KEY, disposition], () => connectionApiService.listUnrouted(disposition), {
    refetchOnWindowFocus: false,
  });

/**
 * Assign REMOVES the row — it leaves the queue for a clinic. Patch, don't
 * refetch: drop it from the cache that holds it (each cache echoes its
 * `disposition`) and decrement the unrouted total in EVERY cached queue copy
 * (all three responses carry all three totals).
 */
const useUnroutedRemoval = <TArgs extends { exchangeId: string }, TResult>(
  fn: (args: TArgs) => Promise<TResult>
) => {
  const queryClient = useQueryClient();

  return useMutation<TResult, unknown, TArgs>(fn, {
    onSuccess: (_data, args) => {
      queryClient.setQueriesData<UnroutedQueueList | undefined>({ queryKey: UNROUTED_KEY }, prev =>
        prev ? applyQueueAssign(prev, args.exchangeId) : prev
      );
    },
    // A refusal persists a new `meta.unrouted[0]` diagnosis before returning 400.
    // Re-read it so the diagnosis remains visible after the mutation error is reset.
    onError: () => queryClient.invalidateQueries(UNROUTED_KEY),
  });
};

export const useAssignUnrouted = () =>
  useUnroutedRemoval(({ exchangeId, clinicId }: { exchangeId: string; clinicId: number }) =>
    connectionApiService.assignUnrouted(exchangeId, clinicId)
  );

/**
 * Close/reopen MOVE the row between queues (Unrouted/Unsupported ⇄ Resolved).
 * With one cache per disposition (item 4), the patch is a move: drop the row
 * from the source cache, insert the patched copy into the target cache when
 * that queue has been loaded, and shift both totals in every cached copy —
 * still no invalidate-and-refetch. The patched row is built from the CACHED
 * copy (the response doesn't repeat display fields) + the response's
 * disposition/reprocessable/change:
 * - history prepends newest first, matching the server's prepend — appending
 *   here left the trail in the opposite order until the next refetch.
 * - `reprocessable` is patched too: it is computed per disposition, and
 *   carrying the old value left a reopened Unsupported row's Reprocess button
 *   stale until a full reload.
 */
const useUnroutedDispositionChange = <TArgs extends { exchangeId: string }>(
  fn: (args: TArgs) => Promise<UnroutedDispositionChangeResult>
) => {
  const queryClient = useQueryClient();

  return useMutation<UnroutedDispositionChangeResult, unknown, TArgs>(fn, {
    onSuccess: data => {
      const caches = queryClient.getQueriesData<UnroutedQueueList | undefined>({
        queryKey: UNROUTED_KEY,
      });
      const source = caches
        .flatMap(([, cached]) => cached?.rows ?? [])
        .find(r => r.exchangeId === data.exchangeId);

      // The acted-on row should always be in a cache (the user clicked it); if
      // it is not, a full re-read is the only honest recovery.
      if (!source || source.disposition === data.disposition) {
        queryClient.invalidateQueries(UNROUTED_KEY);

        return;
      }

      const from = source.disposition as QueueDisposition;
      const to = data.disposition as QueueDisposition;
      const patched: UnroutedExchangeSummary = {
        ...source,
        disposition: data.disposition,
        reprocessable: data.reprocessable,
        history: [data.change, ...source.history],
      };

      queryClient.setQueriesData<UnroutedQueueList | undefined>({ queryKey: UNROUTED_KEY }, prev =>
        prev ? applyQueueMove(prev, { from, to, patched }) : prev
      );
    },
  });
};

export const useCloseUnrouted = () =>
  useUnroutedDispositionChange(({ exchangeId, reason }: { exchangeId: string; reason: string }) =>
    connectionApiService.closeUnrouted(exchangeId, reason)
  );

export const useReopenUnrouted = () =>
  useUnroutedDispositionChange(({ exchangeId, message }: { exchangeId: string; message: string }) =>
    connectionApiService.reopenUnrouted(exchangeId, message)
  );

export const useRunTransmitBackstop = () =>
  useConsoleMutation((vendor: string) => connectionApiService.transmitRun(vendor));

/** Per-vendor "run inbound drain now" — kicks the drain; the console's watch window shows what lands. */
export const useRunInboundDrain = () =>
  useConsoleMutation((vendor: string) => connectionApiService.inboundDrainRun(vendor));

/** Turn automation polling on/off for the caller's own combo (refreshes the console). */
export const useTogglePolling = () =>
  useConsoleMutation((args: { vendor: string; enabled: boolean }) =>
    connectionApiService.togglePolling(args.vendor, args.enabled)
  );

export const useClearTransmitLock = () =>
  useConsoleMutation((vendor: string) => connectionApiService.clearTransmitLock(vendor));

/** Empty the transmit FIFO (ghost cleanup) — Pending rows survive and re-enqueue on the next run. */
export const usePurgeTransmitQueue = () =>
  useConsoleMutation(() => connectionApiService.purgeTransmitQueue());

/** Error-detail modal: pull ONE exchange by id (only when the modal is open). */
export const useConnectorExchange = (exchangeId: string | null) =>
  useQuery(
    [...KEY_BASE, 'exchange', exchangeId],
    () => connectionApiService.fetchExchange(exchangeId as string),
    { enabled: !!exchangeId, refetchOnWindowFocus: false, retry: false }
  );

/** Re-drive ONE exchange (inbound re-dispatch or outbound re-enqueue); refreshes the console. */
export const useRetryExchange = () =>
  useConsoleMutation((args: { exchangeId: string; trace?: boolean }) =>
    connectionApiService.retryExchange(args.exchangeId, args.trace ?? false)
  );

/**
 * Reprocess ONE Unsupported row. The row LEAVES the Unsupported view either way — it
 * becomes Processed under a clinic, or Unrouted with routing candidates it did not have
 * before — so the queue is invalidated rather than patched: the new shape comes from
 * the funnel, not from this response.
 */
export const useReprocessUnsupported = () => {
  const queryClient = useQueryClient();

  return useMutation(
    (args: { exchangeId: string }) => connectionApiService.reprocessExchange(args.exchangeId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(UNROUTED_KEY);
      },
    }
  );
};

/** Mark an activity row reviewed (hide it) or restore it; refreshes the console. */
export const useReviewActivity = () =>
  useConsoleMutation((args: { id: number; reviewed: boolean }) =>
    connectionApiService.reviewActivity(args.id, args.reviewed)
  );

export const useSetTraceConfig = (vendor?: string, integrationId?: number) => {
  const queryClient = useQueryClient();

  return useMutation(
    (body: Parameters<typeof connectionApiService.setTraceConfig>[2]) =>
      connectionApiService.setTraceConfig(vendor!, integrationId!, body),
    {
      // The response IS the fresh config — patch it into the console payload
      // rather than invalidate-and-refetch. setQueriesData (PREFIX match), not
      // setQueryData (exact): the live console key carries the `includeReviewed`
      // flag (`[...CONSOLE_KEY, bool]`), so an exact CONSOLE_KEY write would
      // miss it and the session would never flip to active.
      onSuccess: data => {
        queryClient.setQueriesData<ConnectionApiConsole | undefined>(
          { queryKey: CONSOLE_KEY },
          prev =>
            prev && vendor
              ? { ...prev, traceConfigs: { ...prev.traceConfigs, [vendor]: data } }
              : prev
        );
      },
    }
  );
};
